import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, LogOut, RefreshCw, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "cipg_admin_key";

const STATUSES = ["New", "Contacted", "Closed"];

const STATUS_STYLES = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Contacted: "bg-amber-50 text-amber-700 border-amber-200",
  Closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Admin() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [googleUser, setGoogleUser] = useState(location.state?.user || null);
  const [key, setKey] = useState(sessionStorage.getItem(STORE_KEY) || "");
  const [input, setInput] = useState("");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/consultations`, {
        headers: { "X-Admin-Key": k || "" },
        withCredentials: true,
      });
      setRows(res.data);
      if (k) {
        sessionStorage.setItem(STORE_KEY, k);
        setKey(k);
      }
    } catch {
      setRows(null);
      if (k) {
        sessionStorage.removeItem(STORE_KEY);
        setKey("");
        setError("Invalid passcode or server error.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const err = searchParams.get("error");
    if (err === "not-authorized") setError("This Google account is not authorized for admin access.");
    else if (err) setError("Google sign-in failed. Please try again.");

    const init = async () => {
      if (location.state?.user) {
        await load("");
        setChecking(false);
        return;
      }
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setGoogleUser(me.data);
        await load("");
      } catch {
        const stored = sessionStorage.getItem(STORE_KEY);
        if (stored) await load(stored);
      }
      setChecking(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (e) => {
    e.preventDefault();
    load(input.trim());
  };

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    sessionStorage.removeItem(STORE_KEY);
    setKey("");
    setGoogleUser(null);
    setRows(null);
    setInput("");
  };

  const setStatus = async (id, status) => {
    const prev = rows;
    setRows(rows.map((r) => (r.id === id ? { ...r, status } : r)));
    try {
      await axios.patch(
        `${API}/consultations/${id}/status`,
        { status },
        { headers: { "X-Admin-Key": key || "" }, withCredentials: true }
      );
      toast.success(`Marked as ${status}`);
    } catch {
      setRows(prev);
      toast.error("Could not update status", { description: "Please try again." });
    }
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-slate-100">
      <header className="bg-navy border-b border-navy-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
            <span className="font-display font-bold text-white tracking-tight">Certify IP Global — Requests</span>
          </div>
          <div className="flex items-center gap-4">
            {googleUser && (
              <span data-testid="admin-user-email" className="hidden sm:inline-flex items-center gap-2 text-xs text-slate-400">
                {googleUser.picture && (
                  <img src={googleUser.picture} alt="" className="w-5 h-5 rounded-full" referrerPolicy="no-referrer" />
                )}
                {googleUser.email}
              </span>
            )}
            {rows && (
              <>
                <button
                  data-testid="admin-refresh-button"
                  onClick={() => load(key)}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-slate-300 hover:text-white transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
                <button
                  data-testid="admin-logout-button"
                  onClick={logout}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-slate-300 hover:text-white transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </>
            )}
            <Link to="/" data-testid="admin-back-link" className="text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors">
              Back to site
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {checking ? (
          <div className="flex items-center justify-center gap-3 mt-24 text-slate-500" data-testid="admin-auth-loading">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Checking access…</span>
          </div>
        ) : !rows ? (
          <div className="max-w-sm mx-auto mt-16 bg-white border border-slate-200 p-8">
            <h1 className="font-display font-bold text-navy text-xl tracking-tight mb-2">Admin Access</h1>
            <p className="text-sm text-slate-500 mb-6">Sign in to view consultation requests.</p>

            <button
              data-testid="admin-google-signin-button"
              onClick={signInWithGoogle}
              className="w-full inline-flex items-center justify-center gap-3 bg-white border border-slate-300 text-navy font-semibold px-6 py-3 text-sm hover:bg-slate-50 hover:border-slate-400 transition-colors duration-300 mb-5"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              Sign in with Google
            </button>

            <div className="flex items-center gap-3 mb-5">
              <span className="h-px flex-1 bg-slate-200" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">or use passcode</span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>

            <form onSubmit={login} data-testid="admin-login-form">
              <Input
                type="password"
                data-testid="admin-passcode-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Admin passcode"
                className="rounded-none border-slate-300 h-12 text-sm focus-visible:ring-2 focus-visible:ring-accent mb-4"
              />
              {error && <p data-testid="admin-login-error" className="text-xs text-red-600 mb-4">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold px-6 py-3 text-sm hover:bg-accent disabled:opacity-60 transition-colors duration-300"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                View Requests
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="admin-requests-table-wrap" className="bg-white border border-slate-200">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
              <h1 className="font-display font-bold text-navy text-lg tracking-tight">Consultation Requests</h1>
              <span data-testid="admin-requests-count" className="font-monodata text-xs text-slate-500">{rows.length} total</span>
            </div>
            {rows.length === 0 ? (
              <div className="p-16 text-center text-slate-500">
                <Inbox className="w-8 h-8 mx-auto mb-4 text-slate-300" />
                <p className="text-sm">No consultation requests yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider">Date</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Company</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Phone</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Service</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Records</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id} data-testid={`admin-request-row-${r.id}`}>
                        <TableCell className="font-monodata text-xs whitespace-nowrap text-slate-500">
                          {new Date(r.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm font-medium text-navy whitespace-nowrap">{r.name}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{r.company}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          <a href={`mailto:${r.email}`} className="text-accent hover:underline">{r.email}</a>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{r.phone || "—"}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{r.service}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{r.records || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <Select value={r.status || "New"} onValueChange={(v) => setStatus(r.id, v)}>
                            <SelectTrigger
                              data-testid={`status-select-${r.id}`}
                              className={`h-8 w-[126px] rounded-none text-xs font-semibold border ${STATUS_STYLES[r.status || "New"]}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`status-option-${s.toLowerCase()}-${r.id}`}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-sm max-w-xs truncate" title={r.message}>{r.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
